import { useState } from 'react';
import { PiSignOutLight, PiCaretLeftLight } from 'react-icons/pi';
import { useAuth0 } from '@auth0/auth0-react';
import { SidebarButton } from 'src/components/atoms/buttons/sidebar-button/sidebar-button';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { NotificationsPopover } from 'src/modules/notifications/components/organisms/notifications-popover';
import { SupportButton } from 'src/modules/support/components/molecules/support-button';
import { useSupport } from 'src/modules/support/hooks/provider-bounded/use-support';
import { SidebarGroups } from './sidebar-groups';

// Scope desktop layout to the shared navigation markers without changing mobile.
const desktopSidebarClasses = `
  flex h-full flex-col overflow-hidden border-r border-neutral-200 bg-white px-2 py-3 text-primary-500
  transition-[width] duration-[220ms] ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none
  [&_[data-sidebar-item]]:grid [&_[data-sidebar-item]]:grid-cols-[48px_minmax(0,1fr)]
  [&_[data-sidebar-item]]:items-center [&_[data-sidebar-item]]:justify-start
  [&_[data-sidebar-item]]:gap-0 [&_[data-sidebar-item]]:h-8
  [&_[data-sidebar-item]]:p-0 [&_[data-sidebar-item]]:text-left
  [&_button[data-sidebar-item]]:border-0
  [&_[data-sidebar-icon]]:justify-self-center [&_[data-sidebar-icon]]:shrink-0
  [&_[data-sidebar-label]]:min-w-0 [&_[data-sidebar-label]]:overflow-hidden
  [&_[data-sidebar-label]]:whitespace-nowrap [&_[data-sidebar-label]]:opacity-100
  [&_[data-sidebar-label]]:transition-opacity [&_[data-sidebar-label]]:duration-[120ms]
  [&_[data-sidebar-label]]:ease-[ease] motion-reduce:[&_[data-sidebar-label]]:transition-none
  [&[data-collapsed=true]_[data-sidebar-label]]:pointer-events-none
  [&[data-collapsed=true]_[data-sidebar-label]]:opacity-0
  [&_[data-sidebar-divider]]:absolute [&_[data-sidebar-divider]]:left-3
  [&_[data-sidebar-divider]]:w-6 [&_[data-sidebar-divider]]:border-t
  [&_[data-sidebar-divider]]:border-neutral-200 [&_[data-sidebar-divider]]:opacity-0
  [&_[data-sidebar-divider]]:transition-opacity [&_[data-sidebar-divider]]:duration-[120ms]
  [&_[data-sidebar-divider]]:ease-[ease] motion-reduce:[&_[data-sidebar-divider]]:transition-none
  [&[data-collapsed=true]_[data-sidebar-divider]]:opacity-100
`;

// Preserve the existing preference when migrating from the pinned/overlay layout.
const SIDEBAR_PINNED_STORAGE_KEY = 'lp.ui.sidebarPinned';

const readExpandedPreference = (): boolean => {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem(SIDEBAR_PINNED_STORAGE_KEY) !== 'false';
};

/** One persistent desktop navigation column with a fixed icon track. */
export const DesktopSidebar = () => {
  const { t } = useI18n();
  const [isExpanded, setIsExpanded] = useState(readExpandedPreference);
  const { logout } = useAuth0();
  const { shutdown } = useSupport();
  const isCollapsed = !isExpanded;
  const toggleLabel = t(
    isExpanded ? 'sidebar.collapseMenu' : 'sidebar.expandMenu'
  );

  const toggleExpanded = () => {
    const next = !isExpanded;
    setIsExpanded(next);
    window.localStorage.setItem(SIDEBAR_PINNED_STORAGE_KEY, String(next));
  };

  const handleLogout = () => {
    shutdown();
    void logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <div
      className={`${desktopSidebarClasses} ${isCollapsed ? 'w-16' : 'w-[236px]'}`}
      data-collapsed={isCollapsed}
    >
      <div className="flex flex-none flex-col gap-1">
        <div className="grid h-11 grid-cols-[48px_minmax(0,1fr)] items-center justify-start gap-0 p-0 text-left">
          <img
            src="/logos/icon-light.svg"
            alt="Comos Icon"
            className="h-8 w-8 shrink-0 justify-self-center"
          />
        </div>
        <Tooltip
          content={toggleLabel}
          placement="right"
          contentClassName="mb-2 min-w-[48px] justify-end"
        >
          <Button
            appearance="soft"
            variant="primary"
            size="sm"
            className="!w-12 !p-0"
            data-sidebar-item
            aria-label={toggleLabel}
            aria-expanded={isExpanded}
            aria-controls="desktop-navigation"
            onClick={toggleExpanded}
          >
            <span data-sidebar-icon>
              <PiCaretLeftLight
                aria-hidden="true"
                className={`h-5 w-5 transition-transform duration-[220ms] ease-[ease] motion-reduce:transition-none ${isCollapsed ? 'rotate-180' : ''}`}
                data-sidebar-collapse-icon
              />
            </span>
          </Button>
        </Tooltip>
        <NotificationsPopover
          showLabel
          isCollapsed={isCollapsed}
          buttonClassName="w-full !p-0"
          placement="right-start"
        />
      </div>
      {/* Keep width-consuming scrollbars in the outer gutter, beyond the icons. */}
      <nav
        id="desktop-navigation"
        className="-mr-2 min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain pr-2"
      >
        <SidebarGroups variant="desktop" isCollapsed={isCollapsed} />
      </nav>
      <div className="flex flex-none flex-col gap-1 border-t border-neutral-200 pt-2">
        <SupportButton variant="desktop" isCollapsed={isCollapsed} />
        <SidebarButton
          icon={<PiSignOutLight className="h-5 w-5" />}
          label={t('sidebar.logout')}
          isCollapsed={isCollapsed}
          onClick={handleLogout}
        />
      </div>
    </div>
  );
};
